// Libraries
import { useMemo, useState } from 'react'
// MUI
import { Box, Paper, Typography } from '@mui/material'
// Redux
import { useSelector } from 'react-redux'
import { TimesheetStoreState } from '../../../../store/timesheetStore'
// Components
import Service from './TimesheetServices/Service'
import ServicesPagination from './TimesheetServices/ServicesPagination'

function TravelingSheetPDF(): React.JSX.Element {
	const MAX_SERVICES_TO_SHOW = 5
	/**
	 * Selector to access the timesheet page data from the Redux store.
	 */
	const timesheetPage = useSelector((state: TimesheetStoreState) => state.timesheetPage)

	// Pagination state and logic
	const [currentPage, setCurrentPage] = useState(1)
	const [rowsPerPage, setRowsPerPage] = useState<typeof MAX_SERVICES_TO_SHOW | 5 | 10 | 15>(MAX_SERVICES_TO_SHOW)
	const totalServices = useMemo(() => timesheetPage?.services?.length ?? 0, [timesheetPage])
	const totalPages = useMemo(() => Math.ceil(totalServices / rowsPerPage), [totalServices, rowsPerPage])
	const paginatedServices = useMemo(() => {
		const startIndex = (currentPage - 1) * rowsPerPage
		const endIndex = startIndex + rowsPerPage
		return timesheetPage?.services?.slice(startIndex, endIndex) ?? []
	}, [timesheetPage, currentPage, rowsPerPage])

	/**
	 * Handler for navigating to the next page in the pagination.
	 * Checks if the current page is less than the total pages before incrementing the page number.
	 * This prevents navigating beyond the last page of services.
	 */
	function handleNextPage() {
		if (currentPage < totalPages) {
			setCurrentPage(prevPage => prevPage + 1)
		}
	}

	/**
	 * Handler for navigating to the previous page in the pagination.
	 * Checks if the current page is greater than 1 before decrementing the page number.
	 * This prevents navigating before the first page of services.
	 */
	function handlePreviousPage() {
		if (currentPage > 1) {
			setCurrentPage(prevPage => prevPage - 1)
		}
	}

	return (
		<Paper
			sx={{
				position: 'relative',
				padding: '1.5rem 2.5rem',
				borderRadius: '10px',
				display: 'grid',
				gap: 3,
				boxShadow: 'none',
			}}
		>
			<Typography
				variant='h5'
				sx={{
					fontFamily: 'var(--font-family)',
					fontSize: '1.5rem',
					fontWeight: 600,
					lineHeight: '133.4%',
					color: 'var(--primary-dark)',
				}}
			>
				{timesheetPage?.services?.length} Serviço
				{timesheetPage?.services?.length !== 1 ? 's' : ''}
			</Typography>
			<Box
				sx={{
					position: 'relative',
					alignSelf: 'stretch',
					overflow: 'auto',
					boxShadow: '0 0 0 0.3px rgba(134, 149, 175, 0.15)',
					borderRadius: '4px',
				}}
			>
				<Box
					sx={{
						position: 'relative',
						display: 'grid',
						gridTemplateColumns: 'minmax(320px, 640px) 14.25rem 10.25rem 80px 69px 84px 115px',
						paddingRight: '1.5rem',
						maxWidth: '100%',
					}}
				>
					{/* Original origin, destination and schedule */}
					<Box
						sx={{
							minWidth: '320px',
							width: 'stretch',
							maxWidth: '640px',
							padding: '0.5rem 1rem',
							borderRight: '1px solid rgba(134, 149, 175, 0.15)',
							background: 'rgba(245, 247, 252, 0.50)',
						}}
					>
						<Typography
							variant='body1'
							sx={{
								fontFamily: 'var(--font-family)',
								fontWeight: 600,
								fontSize: '12px',
								lineHeight: '200%',
								letterSpacing: '0.17px',
								color: 'var(--primary-light)',
							}}
						>
							Início e Término (Escalado)
						</Typography>
					</Box>
					{/* Scheduled start and end time */}
					<Box sx={{ width: '14.25rem', padding: '0.5rem 1rem' }}>
						<Typography
							variant='body1'
							sx={{
								display: 'grid',
								fontFamily: 'var(--font-family)',
								fontWeight: 600,
								fontSize: '12px',
								lineHeight: '200%',
								letterSpacing: '0.17px',
								color: 'var(--primary-light)',
							}}
						>
							Hora e Data Início
							<br />
							Hora e Data Fim
						</Typography>
					</Box>
					{/* Vehicle number and load/empty/total kms */}
					<Box
						sx={{
							width: '10.25rem',
							padding: '0.5rem 1rem',
						}}
					>
						<Typography
							variant='body1'
							sx={{
								display: 'grid',
								fontFamily: 'var(--font-family)',
								fontWeight: 600,
								fontSize: '12px',
								lineHeight: '200%',
								letterSpacing: '0.17px',
								color: 'var(--primary-light)',
							}}
						>
							Nr Viatura
							<br />
							Carga / Vazio / Total
						</Typography>
					</Box>
					{/* Lotation */}
					<Box
						sx={{
							maxWidth: '80px',
							padding: '0.5rem 1rem',
							//
						}}
					>
						<Typography
							variant='body1'
							sx={{
								fontFamily: 'var(--font-family)',
								fontWeight: 600,
								fontSize: '12px',
								lineHeight: '200%',
								letterSpacing: '0.17px',
								color: 'var(--primary-light)',
							}}
						>
							Lot
						</Typography>
					</Box>
					{/* Has Notes */}
					<Box
						sx={{
							width: '69px',
							padding: '0.5rem 1rem',
						}}
					>
						<Typography
							variant='body1'
							sx={{
								fontFamily: 'var(--font-family)',
								fontWeight: 600,
								fontSize: '12px',
								lineHeight: '200%',
								letterSpacing: '0.17px',
								color: 'var(--primary-light)',
							}}
						>
							Notas
						</Typography>
					</Box>
					{/* Cleaning status */}
					<Box
						sx={{
							width: '84px',
							padding: '0.5rem 1rem',
						}}
					>
						<Typography
							variant='body1'
							sx={{
								fontFamily: 'var(--font-family)',
								fontWeight: 600,
								fontSize: '12px',
								lineHeight: '200%',
								letterSpacing: '0.17px',
								color: 'var(--primary-light)',
							}}
						>
							Limpeza
						</Typography>
					</Box>
					{/* Import / Export */}
					<Box
						sx={{
							width: '115px',
							padding: '0.5rem 1rem',
						}}
					>
						<Typography
							variant='body1'
							sx={{
								fontFamily: 'var(--font-family)',
								fontWeight: 600,
								fontSize: '12px',
								lineHeight: '200%',
								letterSpacing: '0.17px',
								color: 'var(--primary-light)',
							}}
						>
							Imp / Exp
						</Typography>
					</Box>
				</Box>
				<ul
					style={{
						padding: 0,
						margin: 0,
						listStyleType: 'none',
						position: 'relative',
					}}
				>
					{timesheetPage?.timeSheetId !== 0 &&
						paginatedServices.map((item, index: number) => (
							<Service
								serviceDetail={item}
								instance={(currentPage - 1) * rowsPerPage + index}
								key={item.service.serviceId ?? index}
							/>
						))}
				</ul>

				{/* Pagination */}
				{timesheetPage?.services.length > MAX_SERVICES_TO_SHOW && (
					<ServicesPagination
						currentPage={currentPage}
						totalPages={totalPages}
						totalServices={totalServices}
						rowsPerPage={rowsPerPage}
						onChangeRowsPerPage={number => setRowsPerPage(number)}
						onNextPage={handleNextPage}
						onPreviousPage={handlePreviousPage}
					/>
				)}
			</Box>
		</Paper>
	)
}
export default TravelingSheetPDF
